import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import {
  KpiShipmentStatistic,
  KpiShipmentStatisticFilter,
} from "../../../Models/KpiShipmentStatistic.ts";
import { ShipmentsService } from "../../../services/shipment/shipments.ts";
import { defaultFetchParam, FetchParam } from "../../../Models/FetchParam.ts";
import { useTypes } from "../../../utilities/types.js";

const shipmentsService = new ShipmentsService();

interface ShipmentsProviderProps {
  children: ReactNode;
  filters: KpiShipmentStatisticFilter;
}

interface ShipmentsContextValue {
  data: KpiShipmentStatistic | null;
  loader: boolean;
  filters: KpiShipmentStatisticFilter;
}

const ShipmentsContext = createContext<ShipmentsContextValue | undefined>(
  undefined
);

const ShipmentsProvider: React.FC<ShipmentsProviderProps> = ({
  children,
  filters,
}) => {
  const [data, setData] = useState<KpiShipmentStatistic | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentStatistic> | null>(null);

  const { confirmationBoolOptions } = useTypes();

  const prevFilters = useRef<KpiShipmentStatisticFilter>(filters);

  const getShipments = (): void => {
    setLoader(true);
    const params: FetchParam = {
      ...defaultFetchParam,
      search: [
        {
          selector: "creationDate",
          value: [filters.start, filters.end],
        },
        {
          selector: "stockReleaseCondition",
          value: confirmationBoolOptions.find((x) => x.value === "true")!,
          precise: true,
        },
      ],
    };
    shipmentsService
      .all(params)
      .then((res) => {
        setLoader(false);
        const data: KpiShipmentStatistic = {
          total: res.data.totalElements,
          totalCod: 0,
          totalNoCod: 0,
          statuses: [],
          carrierServices: [],
          regions: [],
        };
        setData(data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getShipments();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getShipments();
  }, []);

  return (
    <ShipmentsContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsContext.
 * Ensures that the context is used only within a ShipmentsProvider.
 */
const useShipmentsContext = (): ShipmentsContextValue => {
  const context = useContext(ShipmentsContext);
  if (!context) {
    throw new Error(
      "useShipmentsContext must be used within a ShipmentsProvider"
    );
  }
  return context;
};

// Export the provider and custom hook for use in other components
export { ShipmentsProvider, useShipmentsContext };
